import React, { useState } from 'react'
import { AppIcons } from '../icons'

interface SignupFormProps {
  onSignup: (email: string, password: string, fullName?: string) => Promise<{ error: any }>
  onSwitchToLogin: () => void
  loading: boolean
  error: string | null
}

export function SignupForm({ onSignup, onSwitchToLogin, loading, error }: SignupFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all required fields')
      return
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match')
      return
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters long')
      return
    }

    if (!acceptTerms) {
      setLocalError('Please accept the terms and conditions')
      return
    }

    const { error } = await onSignup(email, password, fullName)
    if (!error) {
      setShowSuccess(true)
    }
  }

  if (showSuccess) {
    return (
      <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md w-full">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AppIcons.CheckCircle className="w-12 h-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
          <p className="text-gray-600 mb-6">
            We've sent you a confirmation link at <strong>{email}</strong>. 
            Please check your email and click the link to activate your account.
          </p>
          <button
            onClick={onSwitchToLogin}
            className="w-full bg-blue-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-base"
            style={{ minHeight: '44px' }}
          >
            Back to Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-md w-full">
      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
        <p className="text-sm sm:text-base text-gray-600">Join StockTagger.ai to get started</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name (Optional)
          </label>
          <input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="John Doe"
            autoComplete="name"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
            Password *
          </label>
          <input
            id="signup-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            required
          />
        </div>

        <div className="mb-4">
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password *
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
            style={{ minHeight: '44px' }}
            placeholder="Confirm your password"
            autoComplete="new-password"
            required
          />
        </div>

        <div className="mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-1 mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              required
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 underline">
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {(error || localError) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <AppIcons.AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error || localError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4 font-medium text-base"
          style={{ minHeight: '44px' }}
        >
          {loading ? 'Creating account...' : 'Create Account'}
        </button>

        <div className="text-center">
          <span className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign in
            </button>
          </span>
        </div>
      </form>
    </div>
  )
}